import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { SearchRefinementType } from '#/modules/search/constants/refinements/searchRefinementTypes';
import {
  useSearchFiltersByName,
  useSearchGetters,
} from '#/modules/search/contexts/SearchContext';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

export function useSearchSelectFacetOption(filterType: SearchRefinementType) {
  const { locale } = useI18n();
  const { embedSearchWidget } = useEmbedSearchWidget();
  const [searchParams] = useSearchParams();
  const searchParamsRef = useLatest<URLSearchParams>(searchParams);
  const searchFiltersByName = useSearchFiltersByName();

  const navigate = useDeferredNavigate();

  const { getSearchTrackingData } = useSearchAnalytics();

  const { getSearchRadius } = useSearchGetters();

  const select = useCallback(
    (value: string) => {
      const previousFilterValue = searchParamsRef.current.get(filterType);

      const searchRadiusFromState = getSearchRadius();

      searchParamsRef.current.delete(filterType);
      searchParamsRef.current.append(filterType, value);
      document.querySelectorAll(`*[name^=${filterType}]`).forEach((option) => {
        if (option instanceof HTMLInputElement) {
          // eslint-disable-next-line no-param-reassign
          option.checked = Boolean(option.value === value);
        }
      });

      // Toggling a filter should always reset results to the first page
      if (searchParamsRef.current.has('page')) {
        searchParamsRef.current.delete('page');
      }

      trackEvent('Changed Search Filter', {
        filter_name: filterType,
        ...(previousFilterValue && previousFilterValue !== value
          ? { deselected_value: previousFilterValue }
          : {}),
        ...(value ? { selected_value: value } : {}),
        ...getSearchTrackingData(),
      });

      const newRoute = searchGetRoutePathSwitch({
        locale,
        q: searchParamsRef.current.get('q') ?? '',
        radius: searchParamsRef.current.get('radius') ?? searchRadiusFromState,
        filtersByName: {
          ...searchFiltersByName,
          [filterType]: value !== '' ? value : undefined,
        },
        embedSearchWidgetId: embedSearchWidget?.id ?? null,
      });

      void navigate(newRoute, {
        replace: true,
      });
    },
    [
      locale,
      embedSearchWidget?.id,
      filterType,
      getSearchRadius,
      getSearchTrackingData,
      navigate,
      searchFiltersByName,
      searchParamsRef,
    ],
  );

  return select;
}
